import { useState, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { LanguageToggle } from "@/components/LanguageToggle"
import {
  ArrowLeft,
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  Sparkles
} from "lucide-react"
import { cn, formatFileSize } from "@/lib/utils"
import { api } from "@/services/api"
import { LeadCaptureModal } from "@/components/LeadCaptureModal"
import type { FileInfo, LeadFormData } from "@/types"

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_FILES = 5
const ACCEPTED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/markdown",
  "text/plain",
  ".md"
]

export function UploadPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const fileObjectsRef = useRef<Map<string, File>>(new Map())
  const [files, setFiles] = useState<FileInfo[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isRateLimited, setIsRateLimited] = useState(false)
  const [showLeadModal, setShowLeadModal] = useState(false)

  // Initialize session on mount
  const initializeSession = async () => {
    if (sessionId) return sessionId

    const result = await api.startDemo("upload")
    if (result.success && result.data) {
      setSessionId(result.data.id)
      return result.data.id
    }
    if (result.status === 429) {
      setIsRateLimited(true)
      throw new Error("rate_limited")
    }
    throw new Error(result.error || "Failed to initialize session")
  }

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return t("upload.errors.fileTooLarge", { size: formatFileSize(MAX_FILE_SIZE) })
    }

    const isAccepted = ACCEPTED_TYPES.some(type =>
      file.type === type || file.name.toLowerCase().endsWith(type)
    )

    if (!isAccepted && !file.name.match(/\.(pdf|docx|md|txt)$/i)) {
      return t("upload.errors.invalidType")
    }

    return null
  }

  const addFiles = (newFiles: FileList | null) => {
    if (!newFiles) return

    setError(null)

    if (files.length + newFiles.length > MAX_FILES) {
      setError(t("upload.errors.tooManyFiles", { max: MAX_FILES }))
      return
    }

    const newFileInfos: FileInfo[] = []

    Array.from(newFiles).forEach((file) => {
      const validationError = validateFile(file)
      const id = Math.random().toString(36).substring(7)

      fileObjectsRef.current.set(id, file)
      newFileInfos.push({
        id,
        name: file.name,
        size: file.size,
        type: file.type || file.name.split('.').pop() || 'unknown',
        status: validationError ? "error" : "pending",
        progress: 0,
        error: validationError || undefined
      })
    })

    setFiles(prev => [...prev, ...newFileInfos])
  }

  const removeFile = (fileId: string) => {
    fileObjectsRef.current.delete(fileId)
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    addFiles(e.dataTransfer.files)
  }, [files.length])

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files)
    e.target.value = '' // Reset input
  }

  const handleLeadSubmit = async (data: LeadFormData) => {
    await api.captureLead(null, data)
    setShowLeadModal(false)
  }

  const uploadFiles = async () => {
    const validFiles = files.filter(f => f.status !== "error")
    if (validFiles.length === 0) {
      setError(t("upload.errors.noValidFile"))
      return
    }

    setIsUploading(true)
    setError(null)

    try {
      const sid = await initializeSession()

      // Update status to uploading
      setFiles(prev => prev.map(f =>
        f.status !== "error" ? { ...f, status: "uploading" } : f
      ))

      let completedCount = 0

      // Upload each valid file using stored File objects
      for (const fileInfo of validFiles) {
        const file = fileObjectsRef.current.get(fileInfo.id)
        if (!file) continue

        const result = await api.uploadFile(sid, file, (progress) => {
          setFiles(prev => prev.map(f =>
            f.id === fileInfo.id ? { ...f, progress } : f
          ))
        })

        if (result.success) {
          completedCount++
          setFiles(prev => prev.map(f =>
            f.id === fileInfo.id ? { ...f, status: "completed", progress: 100 } : f
          ))
        } else {
          setFiles(prev => prev.map(f =>
            f.id === fileInfo.id ? { ...f, status: "error", error: result.error } : f
          ))
        }
      }

      // Navigate to chat after at least one successful upload
      if (completedCount > 0) {
        setTimeout(() => {
          navigate("/demo/chat", { state: { sessionId: sid, mode: "upload" } })
        }, 1000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try again.")
    } finally {
      setIsUploading(false)
    }
  }

  const getFileIcon = (type: string) => {
    if (type.includes("pdf")) return "📄"
    if (type.includes("word") || type.includes("docx")) return "📝"
    if (type.includes("markdown") || type.includes("md")) return "📑"
    if (type.includes("text") || type.includes("txt")) return "📃"
    return "📎"
  }

  const getStatusIcon = (status: FileInfo["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-success" />
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-500" />
      case "uploading":
      case "processing":
        return <Loader2 className="w-5 h-5 text-primary animate-spin" />
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/demo/select")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("upload.back")}
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-lg hidden sm:block">{t("upload.title")}</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <LanguageToggle />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("upload.pageTitle")}
            </h1>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              {t("upload.pageSubtitle")}
            </p>
          </div>

          {/* Upload Zone */}
          <Card className="mb-6">
            <CardContent className="p-0">
              <div
                className={cn(
                  "upload-zone m-6",
                  isDragging && "drag-over"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.md,.txt"
                  className="hidden"
                  onChange={handleFileInput}
                />
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8 text-primary" />
                  </div>
                  <p className="text-lg font-medium text-foreground mb-2">
                    {t("upload.dropZoneTitle")}
                  </p>
                  <p className="text-sm text-muted">
                    {t("upload.dropZoneSubtitle", { size: formatFileSize(MAX_FILE_SIZE) })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rate Limit Message */}
          {isRateLimited && (
            <div className="mb-6 p-6 rounded-lg bg-primary/5 border border-primary/20 text-center">
              <h3 className="font-semibold text-foreground mb-2">{t("rateLimit.title")}</h3>
              <p className="text-sm text-muted mb-4">{t("rateLimit.message")}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => setShowLeadModal(true)}>
                  {t("rateLimit.contactUs")}
                </Button>
                <Button variant="outline" onClick={() => navigate("/")}>
                  {t("rateLimit.tryLater")}
                </Button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && !isRateLimited && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* File List */}
          {files.length > 0 && (
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-foreground">
                    {t("upload.filesCount", { count: files.length, max: MAX_FILES })}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { fileObjectsRef.current.clear(); setFiles([]) }}
                    disabled={isUploading}
                  >
                    {t("upload.clearAll")}
                  </Button>
                </div>

                <div className="space-y-3">
                  {files.map((file) => (
                    <div
                      key={file.id}
                      className={cn(
                        "flex items-center p-4 rounded-lg border",
                        file.status === "error" ? "bg-red-50 border-red-200" : "bg-background"
                      )}
                    >
                      <span className="text-2xl mr-4">{getFileIcon(file.type)}</span>

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {file.name}
                        </p>
                        <p className="text-sm text-muted">
                          {formatFileSize(file.size)}
                        </p>

                        {/* Progress Bar */}
                        {(file.status === "uploading" || file.status === "processing") && (
                          <div className="mt-2">
                            <div className="progress-bar">
                              <div
                                className="progress-bar-fill"
                                style={{ width: `${file.progress}%` }}
                              />
                            </div>
                            <p className="text-xs text-muted mt-1">
                              {file.status === "uploading" ? t("upload.uploading") : t("upload.processing")}
                            </p>
                          </div>
                        )}

                        {file.error && (
                          <p className="text-sm text-red-600 mt-1">{file.error}</p>
                        )}
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        {getStatusIcon(file.status)}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => removeFile(file.id)}
                          disabled={isUploading}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted">
              {t("upload.filesReady", {
                count: files.filter(f => f.status === "completed").length,
                total: files.length,
              })}
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate("/demo/select")}
                disabled={isUploading}
              >
                {t("upload.cancel")}
              </Button>
              <Button
                onClick={uploadFiles}
                disabled={files.length === 0 || isUploading || files.every(f => f.status === "error")}
                className="min-w-[140px]"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("upload.uploading")}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {t("upload.uploadAndContinue")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>

      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSubmit={handleLeadSubmit}
        sessionDuration={0}
        messageCount={0}
      />
    </div>
  )
}
